/* tslint:disable:component-selector import-spacing no-trailing-whitespace no-inferrable-types */

import {
  Component,
  AfterViewInit,
  Input
}                                from '@angular/core';

import { Logger }                from '@/util/logger';

import { UserLogService }        from '@/services/user.log.service';
import { UserLogViewerMetadata } from '@/screens/ide/user.log.viewer.metadata';
import { Train }                 from '@/models/scenario/train';
import { DecisionPoint }         from '@/models/scenario/decision.point';
import { UserLogDataSource }     from '@/models/userlog/user.log.data.source';
import { basename }              from '@/util/util';


import 'gasparesganga-jquery-loading-overlay';

import {
  GridApi, GridReadyEvent,
  ICellRendererParams,
  RowModelType,
  RowNode
} from "ag-grid-community";


/**
 *
 * @class UserLogComponent
 *
 * @brief This is the main controller/model for a single user log tree.  There may be more
 * than one of these, side-by-side inside the UserLogViewerComponent window.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2021
 *
 */

@Component({
  selector: 'user-log-component',
  template: `
    <div id="user-log-tree-container-{{uuidparent}}" class="user-log-tree-container" style="">
      <ag-grid-angular id="ag-grid-user-log-tree-{{uuidparent}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-user-log-tree"
        [columnDefs]="columnDefs"
        [context]="context"
        [defaultColDef]="defaultColDef"
        [autoGroupColumnDef]="autoGroupColumnDef"
        [rowModelType]="rowModelType"
        [treeData]="true"
        [animateRows]="true"
        [isServerSideGroup]="isServerSideGroup"
        [getServerSideGroupKey]="getServerSideGroupKey"
        [isServerSideGroupOpenByDefault]="isServerSideGroupOpenByDefault"
        [rowData]="rowData"
        (gridReady)="onGridReady($event)"
      >
      </ag-grid-angular>
    </div>
    `
})

export class UserLogComponent implements AfterViewInit {

  /** the left to right ordering of the user log tree panels */
  @Input()  order: number;

  /** the UUID for the parent UserLogViewerComponent, we add our order key to get our own UUID */
  @Input()  uuidparent: string;

  /** set to true after a successful load of the user log tree */
  private loaded: boolean             = false;

  /** the database we are reading in data from */
  public dsn : string                 = '';

  /** the collection in that database we are reading from */
  public collection: string           = '';

  /** the input tag for which scenario this originally came from */
  public inputTag: string            = '';

  /** the train to focus on */
  public train: Train                = null;

  /** the decision point of the train to focus on */
  public dp: DecisionPoint            = null;

  /** the initial data for the nodes */
  public nodes: any[]                 = [
    {
      name: 'User Log (Empty)'
    }
  ];

  /** the API for the widget */
  private          api: GridApi       = null;

  public rowModelType: RowModelType   = "serverSide";

  /** our toggle for full screen or not (F11 / Escape) */
  private isFullScreen: boolean       = false;

  /** pass to AG Grid to allow its call backs to access 'this' */
  public context                     = {

    userLog: this

  };

  /** the default row data */
  public rowData                     = [];

  /* tree/table structure */
  public columnDefs = [
    {
      field: 'id',
      hide: true
    },
    {
      field: 'name',
      hide: true
    },
  ];

  /** the defaults for all columns */
  public defaultColDef               = {
    width:  240,
    filter: 'agTextColumnFilter',
    flex:   1,
  };

  /** ag grid needs some rendering support for the tree (group) column */
  public autoGroupColumnDef          = {
    headerName: 'Context',
    field: 'name',
    cellRendererParams: {
      innerRenderer: (params: ICellRendererParams) => {

        if (params.data.type == 'deployment') {

          if (/\s+\|\s+FAIL\s+/.test(params.data.name)) {

            return `<span  style="color: orangered;">${params.data.name}</span>`;
          }
        }

        if ((params.data.type == 'decision-point-group')||(params.data.type == 'dispatch')||(params.data.type == 'deployment')) {

          if (params.data.depart) {

            return `<span  style="color: limegreen;">${params.data.name}</span>`;

          } else {

            return `<span style="color: grey;">${params.data.name}</span>`;
          }
        }

        return `<span style="color: lightslategray;">${params.data.name}</span>`;
      }
    }
  };

  /** standard constructor */
  constructor(
    private userLogs: UserLogService
  ) {


    Logger.debug('[userlog] creating...');

  }

  /** fetch the Grid API */
  public gridApi():GridApi {

    return this.api;
  }

  /** test if row data item is a parent */
  public isServerSideGroup(item: any) {

    return item.hasChildren;
  }

  /** fetch the parent key for a row data item */
  public getServerSideGroupKey(item: any) {

    return item.id;
  }

  /** test if we should open the group right after loading it. */
  public isServerSideGroupOpenByDefault = (params: any): boolean => {

    const node: RowNode          = params.rowNode;
    let    ulc: UserLogComponent = params.data.context.userLog;

    if (node.data.type == 'simulation') {

      setTimeout(() => {
        ulc.gridApi().ensureNodeVisible(node, 'middle');
      }, 50);

      return true;
    }

    if (node.data.isExpanded === true) {

      setTimeout(() => {
        ulc.gridApi().ensureNodeVisible(node, 'middle');
      }, 50);

      return true;
    }

    /* if train is in focus, open it. */

    if (node.data.type == 'train') {

      if (typeof node.data.initialfocus != 'undefined') {

        setTimeout(() => {
          ulc.gridApi().ensureNodeVisible(node, 'middle');
        }, 50);

        return true;
      }

      return false;
    }

    /* if decision point group is in focus, open it. */

    if (node.data.type == 'decision-point-group') {

      return false;
    }

    /* if dispatch is in focus, open it. */

    if (node.data.type == 'dispatch') {

      return false;
    }

    if (node.data.type == 'deployment') {

      return false;
    }

    /* hard pass! */

    return false;
  }

  public onGridReady(event: GridReadyEvent) {

    this.api       = event.api;


    Logger.debug(`[userlog] ${this.uuidparent}-${this.order} AG Grid is ready.`);

  }

  ngAfterViewInit(): void {

    Logger.debug(`[userlog] init()...`);

  }

  /** empty out the tree */
  public clear() {

    this.nodes = [
      {
        name: 'User Log (Empty)'
      }
    ];

    this.loaded     = false;
    this.dsn        = '';
    this.collection = '';
    this.train      = null;
    this.dp         = null;
    this.inputTag   = '';

    if (this.api != null) {

      this.api.setGridOption('serverSideDatasource', null);
    }
  }

  /** test if this tree is empty or not */
  public empty(): boolean {

    return !this.loaded;
  }

  /** load up the given user log; start with the simulation (root) node, and its trains */
  public load(meta: UserLogViewerMetadata) {

    Logger.debug(`[userlog] ${this.uuidparent}-${this.order} loading: `, meta);

    /* first fetch the root node... */

    const ulc: UserLogComponent = this;

    ulc.clear();

    this.collection = meta.collection;
    this.dsn        = meta.dsn;
    this.train      = meta.train;
    this.dp         = meta.dp;
    this.loaded     = true;

    this.autoGroupColumnDef.headerName = basename(meta.inputTag);

    this.api.setGridOption('autoGroupColumnDef', this.autoGroupColumnDef);

    ulc.api.setGridOption('serverSideDatasource', new UserLogDataSource(meta.collection, this.userLogs, this.context, this.train, this.dp));

  }


  /** check for geometry updates and update children as needed */
  public resize() {

  }

  /** clean up when closing the window */
  public close() {


  }


}
